import React, { useEffect, useState, useCallback } from 'react';
import { Sparkles, CreditCard, RefreshCw } from 'lucide-react';
import { Card, Button, toast } from '../../components/v2/V2Components';
import UsageBar from '../../components/v2/UsageBar';
import SubscriptionService from '../../services/SubscriptionService';
import { appUrl } from '../../utils/appUrl';
import { billingErrorMessage } from '../../utils/billingErrors';

// Friendly labels + display order for the usage categories returned by the API.
const CATEGORY_LABELS = {
  houses: 'Houses',
  rooms: 'Rooms',
  items: 'Items',
  storage: 'Storage',
  scans: 'Document scans',
};
const CATEGORY_ORDER = ['houses', 'rooms', 'items', 'storage', 'scans'];

/**
 * Render a subscription timestamp (renewal / access-until) as a calendar date.
 *
 * `subscription_expires_at` is Stripe's period end or `cancel_at` — an exact UTC
 * instant, typically just after midnight UTC (e.g. 2026-11-17T00:29:21Z). It is
 * formatted in UTC on purpose: in the viewer's local zone, every US viewer would
 * see that Nov 17 as "Nov 16", a day earlier than the date Stripe shows them in
 * the Customer Portal and on their receipt. Exported for its regression test.
 */
export const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return null;
  }
};

export default function Subscription() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await SubscriptionService.getLimits());
    } catch (err) {
      setError(err?.message || 'Could not load your plan.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isPaid = data?.subscription_tier === 'paid';

  const handleUpgrade = async () => {
    setActionLoading(true);
    try {
      const url = await SubscriptionService.createCheckoutSession({
        successUrl: appUrl('/subscription/success'),
        cancelUrl: appUrl('/subscription/cancel'),
      });
      window.location.href = url;
    } catch (err) {
      toast.error(billingErrorMessage(err, 'Could not start checkout. Please try again.'));
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setActionLoading(true);
    try {
      const url = await SubscriptionService.openBillingPortal({
        returnUrl: appUrl('/subscription'),
      });
      window.location.href = url;
    } catch (err) {
      toast.error(billingErrorMessage(err, 'Could not open the billing portal. Please try again.'));
      setActionLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-[var(--text-main)]">Plan &amp; Usage</h1>
        <p className="text-sm text-[var(--text-secondary)]">Manage your subscription and see how much of your plan you&apos;re using.</p>
      </div>

      {loading && <Card className="p-6 text-sm text-[var(--text-secondary)]">Loading your plan…</Card>}

      {error && !loading && (
        <Card className="p-6">
          <p className="text-sm text-[var(--error)] mb-3">{error}</p>
          <Button variant="secondary" leadingIcon={RefreshCw} onClick={load}>Try again</Button>
        </Card>
      )}

      {data && !loading && (
        <>
          <Card className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[var(--primary)]" />
                <div>
                  <div className="text-lg font-semibold text-[var(--text-main)]">
                    {isPaid ? 'Premium' : 'Free'} plan
                  </div>
                  <div className="text-xs text-[var(--text-secondary)] capitalize">
                    {data.subscription_status}
                    {isPaid && formatDate(data.subscription_expires_at)
                      ? ` · ${data.subscription_status === 'canceled' ? 'access until' : 'renews'} ${formatDate(data.subscription_expires_at)}`
                      : ''}
                  </div>
                </div>
              </div>
              {isPaid ? (
                <Button variant="secondary" leadingIcon={CreditCard} onClick={handleManageBilling} loading={actionLoading}>
                  Manage billing
                </Button>
              ) : (
                <Button variant="primary" leadingIcon={Sparkles} onClick={handleUpgrade} loading={actionLoading}>
                  Upgrade to Premium
                </Button>
              )}
            </div>
          </Card>

          <Card className="p-6 space-y-4">
            <h2 className="text-sm font-semibold text-[var(--text-main)]">Usage</h2>
            {CATEGORY_ORDER.map((key) => {
              const cat = data.categories?.[key];
              if (!cat) return null;
              return (
                <UsageBar
                  key={key}
                  label={CATEGORY_LABELS[key]}
                  usage={cat.usage}
                  limit={cat.limit}
                  percentage={cat.percentage}
                  isStorage={key === 'storage'}
                  note={key === 'scans' ? cat.type : undefined}
                />
              );
            })}
          </Card>
        </>
      )}
    </div>
  );
}
